
import React from "react";
import { ArrowRight, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface Step {
  number: string;
  title: string;
  description: string;
}

interface DisclaimerProps {
  title: string;
  description: string;
}

interface HowItWorksSectionProps {
  title: string;
  subtitle: string;
  steps: Step[];
  ctaTitle: string;
  ctaText: string;
  disclaimer?: DisclaimerProps;
}

const HowItWorksSection: React.FC<HowItWorksSectionProps> = ({
  title,
  subtitle,
  steps,
  ctaTitle,
  ctaText,
  disclaimer
}) => {
  const scrollToPricing = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const pricingSection = document.getElementById('planos');
    pricingSection?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-drcash-maastricht-blue mb-6">
            {title}
          </h2>
          <p className="text-drcash-slate-gray max-w-2xl mx-auto">
            {subtitle}
          </p>
          <div className="h-1 w-20 bg-gradient-to-r from-drcash-caribbean-green to-drcash-tiffany-blue rounded-full mx-auto mt-6"></div>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
          {steps.map((step, index) => (
            <div key={index} className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <span className="text-drcash-caribbean-green text-4xl font-bold leading-none">
                  {step.number}
                </span>
                {index < steps.length - 1 && (
                  <ArrowRight className="w-6 h-6 text-drcash-slate-gray hidden md:block" />
                )}
              </div>
              <h3 className="text-xl font-bold text-drcash-maastricht-blue mb-3">{step.title}</h3>
              <p className="text-drcash-slate-gray">{step.description}</p>
            </div>
          ))}
        </div>

        {disclaimer && (
          <Alert className="mb-12 border-drcash-caribbean-green bg-drcash-caribbean-green/5">
            <AlertCircle className="h-5 w-5 text-drcash-caribbean-green" />
            <AlertTitle className="text-drcash-maastricht-blue">{disclaimer.title}</AlertTitle>
            <AlertDescription className="text-drcash-slate-gray">
              {disclaimer.description}
            </AlertDescription>
          </Alert>
        )}
        
        <div className="bg-drcash-lavender-mist rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-drcash-maastricht-blue mb-4">
            {ctaTitle}
          </h3>
          <a 
            href="#planos" 
            onClick={scrollToPricing}
            className="btn-primary inline-flex items-center group mt-6"
          >
            {ctaText}
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
